import { computed, inject, Injector, Type } from '@angular/core';

import {
  concat,
  EMPTY,
  forkJoin,
  Observable,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import environment from '@environments/environment';

import { withCountryEntities } from '@erm/cluster';
import {
  withDegreesEntities,
  withSpecialtiesEntities,
} from '@data-access/profiles/store/features';

import {
  DEFAULT_PROFILE_PAGE,
  DEFAULT_PROFILE_PAGE_PROPS,
  getPrimaryAddress,
  getPrimaryEmail,
  getPrimaryPhones,
  setProfilePageError,
  setProfilePageLoading,
} from '../../constants';
import { ProfileService } from '../../services/profile.service';

import {
  BulkCreateContactAddressesRequest,
  BulkCreateContactDegreesRequest,
  BulkCreateContactEmailsRequest,
  BulkCreateContactPhonesRequest,
  BulkCreateContactSpecialtiesRequest,
  ProfileData,
  ProfileInfo,
  ProfilePageContextType,
  ProfilePageSignalStore,
} from '../../types';
import {
  profileDetailsMapper,
  profileInfoMapper,
  mapUpdateContactRequest,
} from '../../utils';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { withSnackBarFeature } from '@data-access/shared';
import { AdminService } from '../../../../admin/admin.service';
import { Role, RoleResponse } from '@team-management/models/roles';

export const PROFILE_PAGE_SIGNAL_STORE = signalStore(
  withState(() => ({
    ...inject(DEFAULT_PROFILE_PAGE),
    internalRoles: [] as Role[],
    attributeList: [] as string[],
  })),
  withProps((store) => ({
    _profileService: inject(ProfileService),
    _adminService: inject(AdminService),
    _injector: inject(Injector),
    store,
  })),

  withFeature(({ _injector }) => withDegreesEntities(_injector)),
  withFeature(({ _injector }) => withSpecialtiesEntities(_injector)),
  withFeature(({ _injector }) =>
    withCountryEntities(environment.irisApi, _injector),
  ),
  withFeature(() => withSnackBarFeature()),
  withComputed(({ _countriesEntities, _statesEntities, ...store }) => ({
    $loading: computed(() => store.loading()),
    $error: computed(() => store.error()),
    $profileData: computed(() => {
      const _profile = store.profile();
      if (!Object.keys(_profile).length) {
        return {} as ProfileData;
      }
      return _profile;
    }),
    $userUpdatedResult: computed(() => store.userUpdatedResult()),
    $degrees: computed(() => {
      const _degrees = store._degreesEntities();
      if (!_degrees?.length) {
        return [] as WcgMenuOption[];
      }
      return _degrees
        .sort((a, b) => a.localeCompare(b))
        .map((degree) => ({ label: degree, id: degree }));
    }),
    $specialties: computed(() => {
      const _specialties = store._specialtiesEntities();
      if (!_specialties?.length) {
        return [] as WcgMenuOption[];
      }
      return _specialties.map((specialty) => ({
        label: specialty,
        id: specialty,
      }));
    }),
    $countries: computed(() => {
      const data = _countriesEntities();
      if (!data) {
        return {
          data: [],
          total: 0,
        };
      }
      return {
        data: data,
        total: data.total ?? 0,
      };
    }),
    $states: computed(() => {
      const data = _statesEntities();
      if (!data) {
        return {
          data: [],
          total: 0,
        };
      }
      return {
        data: data,
        total: data.total ?? 0,
      };
    }),
    $internalRoles: computed(() => {
      const roles = store.internalRoles();
      if (!roles?.length) {
        return [] as WcgMenuOption[];
      }
      return roles
        .filter((role) => {
          const _lowerName = role.name.toLowerCase();
          return !_lowerName.includes('qc') && !_lowerName.includes('sme');
        })
        .map((role) => ({
          label: role.description,
          id: role.id,
        }));
    }),
    $attributeList: computed(() => {
      const attributes = store.attributeList();
      if (!attributes?.length) {
        return [] as WcgMenuOption[];
      }
      return attributes.map((attribute) => ({
        label: attribute,
        id: attribute,
      }));
    }),
    $isStaff: computed(() => store.isStaff()),
    $statusList: computed(() => {
      return [
        { label: 'Active', id: 'active' },
        { label: 'Inactive', id: 'in_active' },
      ];
    }),
  })),
  withMethods(({ _profileService, _adminService, ...store }) => {
    /*
     * @load Profile
     * */
    const _loadProfile = rxMethod<{ isInternal: boolean; userId?: string }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('general', true),
            error: setProfilePageError('general', null),
          }),
        ),
        switchMap(({ isInternal, userId }) => {
          return (
            isInternal && userId && userId.length > 0
              ? _profileService.getAdminProfile(userId)
              : _profileService.getProfile()
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('general', error),
                  loading: setProfilePageLoading('general', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('general', false),
                }),
              next: (profileData) =>
                patchState(store, {
                  profile: { ...profileInfoMapper(profileData) },
                  error: null,
                }),
            }),
          );
        }),
      ),
    );
    /*
     * @load Profile End
     * */

    /*
     * @Emails
     * */
    const _bulkUpdateEmails = rxMethod<{
      isInternal: boolean;
      payload: BulkCreateContactEmailsRequest;
      userId?: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('emails', true),
            error: setProfilePageError('emails', null),
          }),
        ),
        switchMap(({ isInternal, payload, userId }) => {
          return (
            isInternal && userId && userId.length > 0
              ? _profileService.updateAdminProfileEmails(userId, payload)
              : _profileService.updateProfileEmails(payload)
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('emails', error),
                  loading: setProfilePageLoading('emails', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('emails', false),
                }),
              next: (updatedEmails) =>
                patchState(store, {
                  profile: {
                    ...store.profile(),
                    emails: [...getPrimaryEmail(updatedEmails.emails)],
                    contact_id: updatedEmails.contact_id,
                  },
                  error: setProfilePageError('emails', null),
                }),
            }),
          );
        }),
      ),
    );
    /*
     * @Emails End
     * */

    /*
     * @Phone Update
     * */
    const _bulkUpdatePhones = rxMethod<{
      isInternal: boolean;
      payload: BulkCreateContactPhonesRequest;
      userId?: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('phones', true),
            error: setProfilePageError('phones', null),
          }),
        ),
        switchMap(({ isInternal, payload, userId }) => {
          return (
            isInternal && userId && userId.length > 0
              ? _profileService.updateAdminProfilePhoneNumbers(userId, payload)
              : _profileService.updateProfilePhoneNumbers(payload)
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('phones', error),
                  loading: setProfilePageLoading('phones', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('phones', false),
                }),
              next: (updatedPhones) =>
                patchState(store, {
                  profile: {
                    ...store.profile(),
                    phones: [...getPrimaryPhones(updatedPhones.phones)],
                    contact_id: updatedPhones.contact_id,
                  },
                  error: setProfilePageError('phones', null),
                }),
            }),
          );
        }),
      ),
    );

    /*
     * @Phone Update End
     * */

    /*
     * @Address Update
     * */
    const _bulkUpdateAddresses = rxMethod<{
      isInternal: boolean;
      payload: BulkCreateContactAddressesRequest;
      userId?: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('addresses', true),
            error: setProfilePageError('addresses', null),
          }),
        ),
        switchMap(({ isInternal, payload, userId }) => {
          return (
            isInternal && userId && userId?.length > 0
              ? _profileService.updateAdminProfileAddresses(userId, payload)
              : _profileService.updateProfileAddresses(payload)
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('addresses', error),
                  loading: setProfilePageLoading('addresses', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('addresses', false),
                }),
              next: (addresses) =>
                patchState(store, {
                  profile: {
                    ...store.profile(),
                    addresses: [...getPrimaryAddress(addresses.addresses)],
                  },
                  error: setProfilePageError('addresses', null),
                }),
            }),
          );
        }),
      ),
    );
    /**
     * @Address Update End
     * */

    /*
     * @Profile Info Update
     * */
    const _updateProfileInfo = rxMethod<{
      userId: string | undefined;
      payload: ProfileInfo;
      isInternal: boolean | undefined;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('info', true),
            error: null,
          }),
        ),
        switchMap(({ userId, payload, isInternal }) => {
          // If ALL of these changed, then the operations should be done in this order:
          // update contact & update user attribute -> update role -> update status
          const profile = store.profile();
          const attributeUpdated =
            payload.role.qcFunction.id !== profile.info.role.qcFunction.id;
          const statusUpdated = payload.status !== profile.info.status;
          const roleUpdated =
            payload.role.role.id !== profile.info.role.role.id;
          const _updateContactPayload = mapUpdateContactRequest(
            payload.personalDetails,
            payload.systemIds.additionalContactIds,
            parseInt(payload.systemIds.primaryContactId, 10),
          );
          if (!isInternal) {
            // External user updates their own profile info and only their profile info
            return _profileService
              .updateProfileContact(_updateContactPayload)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error: setProfilePageError('info', error),
                      loading: setProfilePageLoading('info', false),
                    }),
                  finalize: () =>
                    patchState(store, {
                      loading: setProfilePageLoading('info', false),
                    }),
                  next: (response) =>
                    patchState(store, {
                      profile: {
                        ...store.profile(),
                        contact_version: response.version,
                        organizationId: response.organization_id,
                        info: {
                          ...store.profile().info,
                          personalDetails: {
                            ...store.profile().info.personalDetails,
                            ...profileDetailsMapper(response),
                          },
                        },
                      },
                      error: null,
                    }),
                }),
              );
          }
          if (isInternal && !userId) {
            // This should not happen
            return EMPTY;
          }

          // User is internal and updating a user profile (potentially their own)
          const obs: Observable<any>[] = [];
          if (isInternal && userId && attributeUpdated) {
            const newqcFunction = payload.role.qcFunction.id!;
            obs.push(
              _profileService
                .updateAdminProfileUserAttribute(userId, {
                  attribute: newqcFunction,
                })
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      patchState(store, {
                        error: setProfilePageError('general', error),
                        loading: setProfilePageLoading('info', false),
                        userUpdatedResult: null,
                      }),
                    finalize: () =>
                      patchState(store, {
                        loading: setProfilePageLoading('info', false),
                      }),
                    next: (response) =>
                      patchState(store, {
                        profile: {
                          ...store.profile(),
                          info: {
                            ...store.profile().info,
                            role: {
                              ...store.profile().info.role,
                              qcFunction: {
                                id: newqcFunction,
                                label: newqcFunction,
                              },
                            },
                          },
                        },
                        userUpdatedResult: {
                          userId,
                          message: response.message,
                        },
                        error: null,
                      }),
                  }),
                ),
            );
          }
          if (isInternal && userId && roleUpdated) {
            const newRole = payload.role.role;
            obs.push(
              _profileService
                .updateAdminProfileUserRole(userId, { role_id: newRole.id! })
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      patchState(store, {
                        error: setProfilePageError('general', error),
                        loading: setProfilePageLoading('info', false),
                      }),
                    finalize: () =>
                      patchState(store, {
                        loading: setProfilePageLoading('info', false),
                      }),
                    next: () =>
                      patchState(store, {
                        profile: {
                          ...store.profile(),
                          info: {
                            ...store.profile().info,
                            role: {
                              ...store.profile().info.role,
                              role: newRole,
                            },
                          },
                        },
                        userUpdatedResult: { userId, message: '' },
                        error: null,
                      }),
                  }),
                ),
            );
          }
          if (isInternal && userId && statusUpdated) {
            const newStatus = payload.status;
            obs.push(
              _profileService
                .updateAdminProfileUserStatus(userId, { status: newStatus })
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      patchState(store, {
                        error: setProfilePageError('general', error),
                        loading: setProfilePageLoading('info', false),
                      }),
                    finalize: () =>
                      patchState(store, {
                        loading: setProfilePageLoading('info', false),
                      }),
                    next: () =>
                      patchState(store, {
                        profile: {
                          ...store.profile(),
                          info: {
                            ...store.profile().info,
                            status: newStatus,
                          },
                        },
                        userUpdatedResult: { userId, message: '' },
                        error: null,
                      }),
                  }),
                ),
            );
          }
          if (isInternal && userId) {
            obs.push(
              _profileService
                .updateAdminProfileContact(userId, _updateContactPayload)
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      patchState(store, {
                        loading: setProfilePageLoading('info', false),
                        error: setProfilePageError('info', error),
                      }),
                    finalize: () =>
                      patchState(store, {
                        loading: setProfilePageLoading('info', false),
                      }),
                    next: (response) =>
                      patchState(store, {
                        profile: {
                          ...store.profile(),
                          contact_version: response.version,
                          organizationId: response.organization_id,
                          info: {
                            ...store.profile().info,
                            personalDetails: {
                              ...store.profile().info.personalDetails,
                              ...profileDetailsMapper(response),
                            },
                            systemIds: {
                              ...store.profile().info.systemIds,
                              additionalContactIds:
                                response[
                                  'data'
                                ].additional_legacy_ids?.toString() ?? '',
                              primaryContactId: `${response.legacy_id ?? ''}`,
                            },
                          },
                        },
                        error: null,
                      }),
                  }),
                ),
            );
          }
          return concat(...obs);
        }),
      ),
    );
    /*
     * @Profile Info Update
     * */
    /*
     * @Degrees
     * */
    const _updateDegrees = rxMethod<{
      isInternal: boolean;
      degrees: BulkCreateContactDegreesRequest;
      userId?: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('degrees', true),
            error: setProfilePageError('degrees', null),
          }),
        ),
        switchMap(({ isInternal, degrees, userId }) => {
          return (
            isInternal && userId && userId.length > 0
              ? _profileService.updateAdminProfileDegrees(userId, degrees)
              : _profileService.updateProfileDegrees(degrees)
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('degrees', error),
                  loading: setProfilePageLoading('degrees', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('degrees', false),
                }),
              next: (updatedDegrees) =>
                patchState(store, {
                  profile: {
                    ...store.profile(),
                    degrees: [...updatedDegrees.degrees],
                    contact_id: updatedDegrees.contact_id,
                  },
                  error: setProfilePageError('degrees', null),
                }),
            }),
          );
        }),
      ),
    );
    /*
     * @Degrees End
     * */

    /**
     * @Specialties
     */
    const _updateSpecialties = rxMethod<{
      isInternal: boolean;
      specialties: BulkCreateContactSpecialtiesRequest;
      userId?: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('specialties', true),
            error: setProfilePageError('specialties', null),
          }),
        ),
        switchMap(({ isInternal, specialties, userId }) => {
          return (
            isInternal && userId && userId.length > 0
              ? _profileService.updateAdminProfileSpecialties(
                  userId,
                  specialties,
                )
              : _profileService.updateProfileSpecialties(specialties)
          ).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('specialties', error),
                  loading: setProfilePageLoading('specialties', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('specialties', false),
                }),
              next: (updatedSpecialties) =>
                patchState(store, {
                  profile: {
                    ...store.profile(),
                    specialties: [...updatedSpecialties.specialties],
                    contact_id: updatedSpecialties.contact_id,
                  },
                  error: setProfilePageError('specialties', null),
                }),
            }),
          );
        }),
      ),
    );
    /*
     * @Specialties End
     */

    /*
     * @RolesAndAttribute List
     * */
    const _loadRolesAndAttributes = rxMethod<void>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setProfilePageLoading('general', true),
          }),
        ),
        switchMap(() =>
          forkJoin([
            _adminService.getAllInternalRoles(),
            _adminService.getAttributeList(),
          ]).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error: setProfilePageError('general', error),
                  loading: setProfilePageLoading('general', false),
                }),
              finalize: () =>
                patchState(store, {
                  loading: setProfilePageLoading('general', false),
                }),
              next: ([rolesList, attributeList]: [RoleResponse, string[]]) => {
                if (rolesList?.data) {
                  patchState(store, { internalRoles: rolesList.data });
                }
                if (attributeList) {
                  patchState(store, { attributeList });
                }
              },
            }),
          ),
        ),
      ),
    );
    /*
     * @RolesAndAttribute end
     * */

    return {
      loadProfile: (isInternal: boolean, userId?: string) =>
        _loadProfile({ isInternal, userId }),
      loadDegrees: () => store.setDegrees(),
      loadSpecialties: () => store.setSpecialties(),
      loadCountries: () => store.setCountries({ pageIndex: 1, pageSize: 300 }),
      loadStates: (countryId: string) => store.setStates({ countryId }),
      loadRolesAndAttributes: () => _loadRolesAndAttributes(),
      clearProfile: () => patchState(store, { ...DEFAULT_PROFILE_PAGE_PROPS }),
      updateEmails: (
        payload: BulkCreateContactEmailsRequest,
        isInternal?: boolean,
        userId?: string,
      ) => _bulkUpdateEmails({ isInternal: !!isInternal, payload, userId }),
      updatePhones: (
        payload: BulkCreateContactPhonesRequest,
        isInternal?: boolean,
        userId?: string,
      ) => _bulkUpdatePhones({ isInternal: !!isInternal, payload, userId }),
      updateAddresses: (
        payload: BulkCreateContactAddressesRequest,
        isInternal?: boolean,
        userId?: string,
      ) => _bulkUpdateAddresses({ isInternal: !!isInternal, payload, userId }),
      updateDegrees: (
        payload: BulkCreateContactDegreesRequest,
        isInternal?: boolean,
        userId?: string,
      ) =>
        _updateDegrees({ isInternal: !!isInternal, degrees: payload, userId }),
      updateSpecialties: (
        payload: BulkCreateContactSpecialtiesRequest,
        isInternal?: boolean,
        userId?: string,
      ) =>
        _updateSpecialties({
          isInternal: !!isInternal,
          specialties: payload,
          userId,
        }),
      updateProfileInfo: (
        payload: ProfileInfo,
        isInternal?: boolean,
        userId?: string,
      ) => _updateProfileInfo({ userId: userId, payload, isInternal }),
      displayErrorMessage: (message: string) =>
        store.displaySnackBar(message, 'Close', 5000),
      setIsStaff: (isStaff: boolean) => patchState(store, { isStaff }),
      clearSectionError: (type: ProfilePageContextType) => {
        const current = store.error();
        if (current?.type === type) {
          patchState(store, { error: null });
        }
      },
    };
  }),
) satisfies Type<ProfilePageSignalStore>;
